import { LoadingButton } from "@mui/lab";
import { Chip, Stack } from "@mui/material";
import Divider from "components/Divider/Divider";
import { FormBuilder } from "components/FormBuilder/FormBuilder";
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { OnlineMerchantManagement } from "../OnlineMerchantManagement";
import { checkNonMoneyDataForTable, trimObject } from "utils/utils";
import SearchFieldButtonBar from "components/SearchFieldButtonBar/SearchFieldButtonBar";
import Datatable from "components/Datatable/Datatable";
import merchantManagement from "routes/merchantManagement";
import Spin from "components/Spin/Spin";
import FunctionalButtonBar from "components/FunctionalButtonBar/FunctionalButtonBar";
import useDialogManager from "utils/useDialogManager/useDialogManager";
import useSnackbarMAS from "utils/useSnackbarMAS/useSnackbarMAS";
import useColors from "utils/useColors/useColors";
import usePageWrapper from "utils/usePageWrapper/usePageWrapper";

const DEFAULT_DATA = {
  total: 0,
  page: 0,
  size: 10,
  data: [],
}

/**
 * @param {Object} props 
 * @param {Function<void>} props.redirectFn
 * @param {import("react-hook-form").UseFormReturn} props.formProps
 */
const OnlineMerchantManagementEmployeeSection = ({ redirectFn, formProps }) => {
  const c = useColors();
  const { t: tLocal } = useTranslation("translation", { keyPrefix: "form.onlineMerchantManagement.merchant.employeeSection" });
  const { t: tCommon } = useTranslation();
  const { snackbarSuccess, snackbarError } = useSnackbarMAS();
  const { handleApi, PageWrapper } = usePageWrapper();

  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [isSearch, setIsSearch] = useState(false);
  const [data, setData] = useState(structuredClone(DEFAULT_DATA));
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeInfoRef = useRef({
    currentpage: 0,
    pagesize: 20,
  });

  const isLoading = isFirstLoading || isSearching;
  const pagination = useMemo(() => {
    return {
      currentPage: currentPage,
      pageSize: pageSize,
      pageSizeOptions: [10, 20, 30, 40],
    };
  }, [currentPage, pageSize]);

  useEffect(() => {
    let isOudated = false;
    const getIsOudated = () => isOudated;

    fetchData(getIsOudated, { isInit: true });

    return () => {
      isOudated = true;
    };
  }, []);

  const fetchData = async (getIsOutdated, { isInit, isSearch } = {}) => {
    const formValues = formProps.getValues();
    if (isSearch) {
      const isValid = await formProps.trigger();
      if (!isValid) return;
    }

    setIsFirstLoading(!!isInit);
    setIsSearching(true);
    setIsSearch(!!isSearch);

    const result = await OnlineMerchantManagement.Api.getEmployeeList(
      trimObject({

        page: pageSizeInfoRef.current.currentpage,
        size: pageSizeInfoRef.current.pagesize,
        ...formValues,
      }),
    )
    if (getIsOutdated && getIsOutdated()) return;

    if (!handleApi(result)) {
      snackbarError({ message: tLocal("toast.failed") });
      setData(DEFAULT_DATA);
    } else {
      const response = result.data;
      setData(response);
      setCurrentPage(response.page);
      setPageSize(response.size);
      pageSizeInfoRef.current.currentpage = response.page;
      pageSizeInfoRef.current.pagesize = response.size;
    }

    setIsFirstLoading(false);
    setIsSearching(false);
  }

  const searchData = () => fetchData(undefined, { isSearch: true });

  const changePasswordFormProps = useForm({ mode: "onSubmit" });
  const { dialog, openDialog } = useDialogManager({
    title: tCommon("page.dialogs.changePassword.title"),
    content: <OnlineMerchantManagement.Components.EmployeeChangePasswordDialog
      formProps={changePasswordFormProps}
    />,
    onPositiveAction: async () => {
      const data = changePasswordFormProps.getValues();
      if (!(await changePasswordFormProps.trigger())) {
        return false;
      }
      const changePasswordResult = await OnlineMerchantManagement.Api.changeEmployeePassword(OnlineMerchantManagement.PayloadBuilder.updateEmployeePassword(data));
      if (!handleApi(changePasswordResult)) {
        const resCode = changePasswordResult?.data?.responseCode;
        snackbarError({
          message: tCommon([
            `page.dialogs.changePassword.msgFailed.${resCode}`,
            "page.dialogs.changePassword.msgFailed"
          ])
        });
        return false;
      }
      snackbarSuccess({ message: tCommon("page.dialogs.changePassword.msgSuccess") });
      return true;
    },
    onOpen: () => {
      changePasswordFormProps.resetField("password");
      return true;
    },
    config: {
      positiveActionText: tCommon("component.simple.dialog.action.3"),
      negativeActionText: tCommon("component.simple.dialog.action.4"),
    }
  });

  const columns = [
    {
      key: "id",
      title: tLocal("column.title.id"),
      sorter: false,
      render: (data, key) => checkNonMoneyDataForTable(data, key)
    },
    {
      key: "firstname",
      title: tLocal("column.title.firstname"),
      sorter: false,
      render: (data, key) => checkNonMoneyDataForTable(data, key)
    },
    {
      key: "status",
      title: tLocal("column.title.status"),
      sorter: false,
      render: (data, key) => checkNonMoneyDataForTable(data, key)
    },
    {
      key: "permission",
      title: tLocal("column.title.permission"),
      sorter: false,
      render: (data, key) => {
        const CHIP_STYLES = {
          FINANCE: {
            color: "#722ed1",
            backgroundColor: "#f9f0ff",
            borderColor: "#d3adf7",
          },
          PAYMENTLINK: {
            color: "#fa8c16",
            backgroundColor: "#fff7e6",
            borderColor: "#ffd591",
          },
          MERCHANT_ADMIN: {
            color: c["greyLightFFF"],
            backgroundColor: "#36bfba",
            borderColor: "#197572",
          },
          MERCHANT: {
            color: "#eb2f96",
            backgroundColor: "#fff0f6",
            borderColor: "#ffadd2",
          },
        };
        const PERM_ORDER = ["FINANCE", "PAYMENTLINK", "MERCHANT", "MERCHANT_ADMIN"];
        const permissionList = data[key]?.split(",")?.sort((a, b) => PERM_ORDER.indexOf(a) - PERM_ORDER.indexOf(b))?.filter((e) => Object.keys(CHIP_STYLES).includes(e));

        return <Stack direction="row" spacing={1}>
          {permissionList?.map((item) => (
            <Chip
              key={item}
              label={item}
              variant="outlined"
              size="small"
              style={CHIP_STYLES[item]}
            />)
          )}
        </Stack>
      }
    },
    {
      key: "action",
      title: tLocal("column.title.action"),
      sorter: false,
      render: (data) => {
        return (
          <Stack direction="row" spacing={1}>
            <LoadingButton
              variant="text-only"
              onClick={() => {
                changePasswordFormProps.setValue("id", data.id);
                openDialog();
              }}
            >
              {tLocal("button.changePassword")}
            </LoadingButton>
          </Stack>
        );
      },
    },
  ].map((column, index) => ({ ...column, index: index }));

  const navigateToCreate = () => {
    const path = "/merchant-management/employees/add-online-employee";
    const payload = {
      name:
        merchantManagement
          .find((tab) => tab.tabName === "Employees")
          .page.find((page) => page.path === path).name,
      path: path,
      state: {
        merchantId: formProps.getValues("merchant_id"),
      }
    };
    redirectFn("redirectPage", payload);
  };

  const navigateToDetail = (data) => {
    const path = "/merchant-management/employees/online-employee-detail";
    const payload = {
      name:
        merchantManagement
          .find((tab) => tab.tabName === "Employees")
          .page.find((page) => page.path === path).name + data.id,
      path: path,
      state: {
        employeeId: data.id,
      },
    };
    redirectFn("redirectPage", payload);
  };

  const isIdEntry = useWatch({ control: formProps.control, name: "employeeId" });
  const isDisabled = isIdEntry;

  /** @type {Record<string, import("components/FormBuilder/lib/FormBuilderFieldBuilder").formBuilderFieldConfig>} */
  const fieldsConfig = {
    employeeId: {
      fieldType: "textInput",
      fieldName: "employeeId",
      label: tLocal("search.label.employeeId"),
      config: {
        customOnEnterDownFn: searchData,
      }
    },
    name: {
      fieldType: "textInput",
      fieldName: "name",
      label: tLocal("search.label.name"),
      disabled: isDisabled,
      config: {
        customOnEnterDownFn: searchData,
      }
    },
    email: {
      fieldType: "textInput",
      fieldName: "email",
      label: tLocal("search.label.email"),
      disabled: isDisabled,
      config: {
        // rules: ["validEmail"], //TODO enable?
        customOnEnterDownFn: searchData,
      }
    },
    status: {
      fieldType: "selection",
      fieldName: "status",
      label: tLocal("search.label.status"),
      disabled: isDisabled,
      config: {
        options: OnlineMerchantManagement.Options.accountStatus,
        customOnEnterDownFn: searchData,
      }
    },
  }

  return (
    <Stack direction="column" gap={2} padding="24px">
      {dialog}
      <FormBuilder.Row itemsPerRow={4}>
        {FormBuilder.buildFields({ formProps, t: tCommon, fields: Object.values(fieldsConfig) })}
      </FormBuilder.Row>

      <SearchFieldButtonBar
        loading={isLoading}
        resetBtn
        handleReset={() => {
          formProps.reset()
          fetchData()
        }}
        searchBtn
        handleSearch={searchData}
      />
      <Divider margin={16} />
      <FunctionalButtonBar
        loading={isLoading}
        createBtn
        handleCreate={navigateToCreate}
        refreshBtn
        handleRefresh={searchData}
      />
      <Stack
        direction="column"
        sx={{
          position: "relative", // for spin
          ...isFirstLoading && { height: "150px", minHeight: "150px" }
        }}>
        <PageWrapper noPadding isLoading={isFirstLoading} >
          <Datatable
            navigateToDetail={navigateToDetail}
            columns={columns}
            // columnsPreference={columnsArray}
            datasource={data.data}
            total={data.total}
            pagination={pagination}
            setCurrentPage={setCurrentPage}
            setPageSize={setPageSize}
            pageSizeInfoRef={pageSizeInfoRef}
            handleSearch={searchData}
            search={isSearch}
          />
          {isSearching && <Spin type={"search"} />}
        </PageWrapper>
      </Stack>
    </Stack>
  )
}

export default OnlineMerchantManagementEmployeeSection
